import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AccessPage = () => {
  const [teams, setTeams] = useState([]);
  const [roles, setRoles] = useState([]);
  const [accessList, setAccessList] = useState([]);
  const [formVisible, setFormVisible] = useState(false);
  const [editAccess, setEditAccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchTeams();
    fetchAccessData();
  }, []);

  // Fetch Teams
  const fetchTeams = async () => {
    try {
      const response = await fetch("http://hr-onboarding-website-backend-LB-1197701009.us-east-1.elb.amazonaws.com:5000/teams");
      if (!response.ok) throw new Error("Failed to fetch teams");
      const data = await response.json();
      setTeams(data || []);
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Fetch Roles
  const fetchRoles = async (teamId) => {
    if (!teamId) return;
    try {
      const response = await fetch(`http://hr-onboarding-website-backend-LB-1197701009.us-east-1.elb.amazonaws.com:5000/roles/${teamId}`);
      if (!response.ok) throw new Error("Failed to fetch roles");
      const data = await response.json();
      setRoles(data || []);
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Fetch Access Data
  const fetchAccessData = async () => {
    try {
      setLoading(true);
      const response = await fetch("http://hr-onboarding-website-backend-LB-1197701009.us-east-1.elb.amazonaws.com:5000/access");
      const data = await response.json();
      if (response.ok) {
        setAccessList(data.accessList);
      } else {
        setError(data.message);
      }
    } catch (error) {
      setError("Failed to fetch access list");
    } finally {
      setLoading(false);
    }
  };

  // Form Validation Schema
  const schema = yup.object().shape({
    roleId: yup.string().required("Role is required"),
    teamId: yup.string().required("Team is required"),
    accessTabs: yup.array().min(1, "Select at least one tab"),
  });

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
  });

  // Handle Form Submission (Create or Update)
  const onSubmit = async (data) => {
    const payload = {
      roleId: data.roleId,
      teamId: [data.teamId],
      accessTabs: data.accessTabs || [],
    };

    try {
      setLoading(true);
      const url = editAccess
        ? `http://hr-onboarding-website-backend-LB-1197701009.us-east-1.elb.amazonaws.com:5000/access/${editAccess._id}`
        : "http://hr-onboarding-website-backend-LB-1197701009.us-east-1.elb.amazonaws.com:5000/access";
      const method = editAccess ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast.success(
          editAccess ? "Access updated successfully" : "Access assigned successfully"
        );
        setFormVisible(false);
        setEditAccess(null);
        reset();
        fetchAccessData();
      } else {
        toast.error("Failed to process access request");
      }
    } catch (error) {
      toast.error("Error processing access");
    } finally {
      setLoading(false);
    }
  };

  // Open Edit Modal
  const handleEdit = (access) => {
    setEditAccess(access);
    setValue("roleId", access.roleId?._id || "");
    setValue("teamId", access.teamId?.[0]?._id || "");
    setValue("accessTabs", access.accessTabs || []);
    setFormVisible(true);
  };

  return (
    <div className="p-6">
      <ToastContainer />
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Access Management</h2>
        <button
          onClick={() => {
            setFormVisible(true);
            setEditAccess(null);
            reset();
          }}
          className="bg-blue-500 text-white py-2 px-4 rounded"
        >
          Assign Access
        </button>
      </div>

      {formVisible && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">
              {editAccess ? "Update Access" : "Assign Access"}
            </h3>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="block font-medium mb-1">Select Team</label>
                <select
                  className="border border-gray-300 rounded p-2 w-full"
                  {...register("teamId")}
                  onChange={(e) => fetchRoles(e.target.value)}
                >
                  <option value="">Select a Team</option>
                  {teams.map((team) => (
                    <option key={team._id} value={team._id}>
                      {team.teams}
                    </option>
                  ))}
                </select>
                {errors.teamId && <p className="text-red-500">{errors.teamId.message}</p>}
              </div>

              <div>
                <label className="block font-medium mb-1">Select Role</label>
                <select className="border border-gray-300 rounded p-2 w-full" {...register("roleId")}>
                  <option value="">Select a Role</option>
                  {roles.map((role) => (
                    <option key={role._id} value={role._id}>
                      {role.roleName}
                    </option>
                  ))}
                </select>
                {errors.roleId && <p className="text-red-500">{errors.roleId.message}</p>}
              </div>

              <div>
                <label className="block font-medium mb-1">Select Access Tabs</label>
                <div className="flex flex-wrap gap-2">
                  {["OnBoarding Deck", "Employee Exit Deck"].map((tab) => (
                    <label key={tab} className="flex items-center space-x-2">
                      <input type="checkbox" value={tab} {...register("accessTabs")} />
                      <span>{tab}</span>
                    </label>
                  ))}
                </div>
                {errors.accessTabs && <p className="text-red-500">{errors.accessTabs.message}</p>}
              </div>

              <div className="flex justify-end space-x-2">
                <button type="button" onClick={() => setFormVisible(false)} className="px-4 py-2 bg-gray-500 text-white rounded">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-blue-500 text-white rounded">
                  {editAccess ? "Update" : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <table className="w-full border-collapse border border-gray-300 mt-2">
        <thead>
          <tr>
            <th className="border p-2">Role</th>
            <th className="border p-2">Teams</th>
            <th className="border p-2">Access Tabs</th>
            <th className="border p-2">Action</th>
          </tr>
        </thead>
        <tbody>
          {accessList.map((access) => (
            <tr key={access._id}>
              <td className="border p-2">{access.roleId?.roleName || "N/A"}</td>
              <td className="border p-2">{access.teamId?.map(team => team.teams).join(", ") || "N/A"}</td>
              <td className="border p-2">{access.accessTabs?.join(", ") || "No Access"}</td>
              <td className="border p-2">
                <button onClick={() => handleEdit(access)} className="text-blue-500">Edit</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AccessPage;
